import { setStoredRayId, setUserStatus } from 'actions/user-actions'
import { getWebRootUrl } from 'constants/api'
import { LocalStorageAlerts, LocalStorageVars } from 'constants/global'
import store from 'redux/store'
import { UserStatus } from 'types/global'
import Storage from './storage'
import 'navigator.locks'
import UserApi from 'api/rest/user'
import { getRayId, makeErrorWithContext } from 'utils'

const TOKEN_LIFESPAN_IN_SECONDS = 900 // 15 minutes
const GET_AUTH_TOKEN_ENDPOINT = `${getWebRootUrl()}/Account/Tokens?clientId=clubspark-app&lifetimeSeconds=${TOKEN_LIFESPAN_IN_SECONDS}`

export class Auth {
  private _fetch: typeof window.fetch
  private _authToken: null | Promise<string>
  private _refreshId: null | number
  private _refreshController: null | AbortController

  constructor() {
    this._authToken = null
    this._refreshId = null
    this._refreshController = null
    this._fetch = window.fetch.bind(window)
    this.setStorageAlertsListener()
  }

  private setStorageAlertsListener() {
    window.addEventListener('storage', event => {
      switch (event.key) {
        case LocalStorageVars.AUTH_TOKEN:
          if (event.newValue) {
            if (this._refreshController) {
              this._refreshController.abort()
              this._refreshController = null
            }
            if (this._refreshId) clearTimeout(this._refreshId)
            const jwt = event.newValue
            this._authToken = Promise.resolve(jwt)
            this.setTokenRefresh(jwt)
          } else {
            this._authToken = null
            this._refreshController = null
            if (this._refreshId) clearTimeout(this._refreshId)
          }
          break
        case LocalStorageAlerts.ALERT_LOGOUT:
          if (event.newValue) {
            UserApi.silentSignOut()
          }
          break
        case LocalStorageAlerts.ALERT_LOGIN:
          if (event.newValue) {
            window.location.reload()
          }
          break
        default:
          break
      }
    })
  }

  private async requestJWToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      this._fetch(GET_AUTH_TOKEN_ENDPOINT, {
        method: 'GET',
        credentials: 'include',
      })
        .then(response => {
          if (response.ok) {
            const authToken = response.headers.get('x-api-token')

            if (!authToken) {
              reject(new Error('No token returned from server'))
            } else {
              Storage.saveAuthToken(authToken)
              resolve(authToken)
            }
          } else {
            const err = makeErrorWithContext(response, { message: 'Failed to get auth token' })

            if (err.status === 401) {
              store.dispatch(setUserStatus(UserStatus.UNAUTHENTICATED))
              err.name = UserStatus.UNAUTHENTICATED
            }
            reject(err)
          }
        })
        .catch(error => {
          store.dispatch(setUserStatus(UserStatus.UNAUTHENTICATED))
          error.name = UserStatus.UNAUTHENTICATED
          error.status = 401
          reject(error)
        })
    })
  }

  private async refreshToken() {
    this._refreshController = new AbortController()
    try {
      await navigator.locks.request(
        'token_refresh',
        { signal: this._refreshController.signal },
        async lock => {
          // The lock has been acquired.
          const jwt = await this.requestJWToken()
          // replace old token, store new token and set new refresh
          Storage.saveAuthToken(jwt)
          this._authToken = Promise.resolve(jwt)
          this.setTokenRefresh(jwt)
          // Now the lock will be released.
        }
      )
    } catch (err) {
      const error = err as Error
      if (error.name === 'AbortError') {
        // The request aborted before it could be granted.
        console.warn('Token refresh aborted: ', error)
      } else {
        console.error('Token refresh failed: ', error)
      }
    } finally {
      this._refreshController = null
    }
  }

  async getAuthToken(): Promise<string> {
    if (this._authToken) return this._authToken

    let storedToken = Storage.getAuthToken()

    if (storedToken && this.isTokenValid(storedToken)) {
      this.setTokenRefresh(storedToken)
      return (this._authToken = Promise.resolve(storedToken))
    } else {
      this.invalidateAuthToken()
    }

    const token = await this.requestJWToken()
    this.setTokenRefresh(token)
    Storage.setAlertRequest('ALERT_LOGIN')
    return (this._authToken = Promise.resolve(token))
  }

  private getTokenExpirationInMS(token: string): number {
    var base64Url = token.split('.')[1]
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )

    return JSON.parse(jsonPayload).exp * 1000
  }

  private isTokenValid(token: string) {
    return this.getTokenExpirationInMS(token) > Date.now()
  }

  private setTokenRefresh(token: string) {
    const msToExpiration = this.getTokenExpirationInMS(token) - Date.now()
    this._refreshId = window.setTimeout(this.refreshToken.bind(this), msToExpiration - 60000)
  }

  public makeAuthHeader(token: string) {
    return `ClubSpark-Auth ${token}`
  }

  public fetch: typeof window.fetch = async (input, options) => {
    const authToken = await this.getAuthToken()

    if (!options) options = {}

    const headers = options?.headers || {}
    options = { ...options, headers: { ...headers, authorization: this.makeAuthHeader(authToken) } }

    return this._fetch(input, options).then(response => {
      const rayId = getRayId(response)

      if (rayId) store.dispatch(setStoredRayId(rayId))

      if (response.status === 401) {
        store.dispatch(setUserStatus(UserStatus.UNAUTHENTICATED))
      }
      return response
    })
  }

  public invalidateAuthToken() {
    Storage.clearAuthToken()
    this._authToken = null
  }
}

export default new Auth()
